// The Home queue's rows (spec 044 D2): pure — no Firestore, no clock of its own.
// services/queue.js runs the reads and hands documents in; pages/Queue renders
// what comes out. Every "now" is a parameter so the tests can pin it.
import { toDate, kindLabel, truncate } from "./ticketPresentation";
import { ticketNumberLabel } from "./inboxPresentation";
import { toVerificationRow } from "./verificationPresentation";
import { periodLabel, periodRange } from "./payCycle";
import { hasPermission, permissions } from "../config/permissions";

// A lesson is 25 or 55 minutes (createBooking.js ALLOWED_DURATIONS), so a
// Pending lesson that STARTED this long ago has ended, whatever its length.
// Filtering on startTimeUTC is what lets the query use the existing
// (status, startTimeUTC) index — the same trick functions/lessonLifecycle.js uses.
export const LESSON_MAX_MINUTES = 55;
export const ROWS_PER_GROUP = 5;
export const BOOKINGS_WINDOW = 25;
export const TICKETS_WINDOW = 100;
export const VERIFY_WINDOW = 100;

export const pendingCutoff = (now) => new Date(now.getTime() - LESSON_MAX_MINUTES * 60 * 1000);

// Amounts are E£ and a 25-minute lesson earns half an hour's salary, so halves are real
// (spec 023 D6). At most two decimals and no trailing ".00" on a whole amount (047 D79):
// `money` is the one rule for every figure on the dashboard, whatever the symbol in front.
export const money = (n) => Number(n || 0).toLocaleString("en-GB", { maximumFractionDigits: 2 });
export const egp = (n) => `E£${money(n)}`;

// "<1m" | "12m" | "3h" | "5d" | "-"
export const ageLabel = (value, now) => {
  const date = toDate(value);
  if (!date) return "-";
  const minutes = Math.floor((now.getTime() - date.getTime()) / 60000);
  if (minutes < 1) return "<1m";
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 48) return `${hours}h`;
  return `${Math.floor(hours / 24)}d`;
};

// Month names are our own: ICU 72+ (Node 20+, current Chrome) abbreviates
// September as "Sept" in en-GB, and payCycle.js already prints "Sep". Intl is
// used only for what it is good at — the weekday and the zone conversion. The
// day goes through Number() for the same kind of reason: asking for a numeric
// day beside a numeric month makes ICU resolve the pair to en-GB's dd/MM
// pattern, which pads to "08 Sep" where every other date on the dashboard
// reads "8 Sep".
export const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// An empty or unknown zone (a user document's timeZone is not validated) falls back to the
// reader's zone: a RangeError here would blank the whole dashboard, not one tab (044 D36).
export const parts = (date, options) => {
  let format;
  try {
    format = new Intl.DateTimeFormat("en-GB", options);
  } catch (e) {
    if (!(e instanceof RangeError)) throw e;
    format = new Intl.DateTimeFormat("en-GB", { ...options, timeZone: undefined });
  }
  return Object.fromEntries(format.formatToParts(date).map((p) => [p.type, p.value]));
};

// "Tue 8 Sep, 18:30" — the reader's zone unless one is given (tests pin UTC).
export const whenLabel = (value, { timeZone } = {}) => {
  const date = toDate(value);
  if (!date) return "-";
  const p = parts(date, {
    weekday: "short",
    day: "numeric",
    month: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
    timeZone,
  });
  return `${p.weekday} ${Number(p.day)} ${MONTHS[Number(p.month) - 1]}, ${p.hour}:${p.minute}`;
};

// "8 Sep 2026" — a date without a time, for "submitted" and the tables' date cells (047 D81).
export const dayLabel = (value, { timeZone } = {}) => {
  const date = toDate(value);
  if (!date) return "-";
  const p = parts(date, { day: "numeric", month: "numeric", year: "numeric", timeZone });
  return `${Number(p.day)} ${MONTHS[Number(p.month) - 1]} ${p.year}`;
};

// "18:30" — 24-hour, like every time on the dashboard (047 D81).
export const timeLabel = (value, { timeZone } = {}) => {
  const date = toDate(value);
  if (!date) return "-";
  const p = parts(date, { hour: "2-digit", minute: "2-digit", hourCycle: "h23", timeZone });
  return `${p.hour}:${p.minute}`;
};

// "8 Sep 2026, 18:30" — where a page once asked ICU for dateStyle "medium" (047 D81).
export const dayTimeLabel = (value, options) => {
  const day = dayLabel(value, options);
  return day === "-" ? "-" : `${day}, ${timeLabel(value, options)}`;
};

// Same aliasing Inbox.js and Lessons.js apply to a user doc with no single
// guaranteed display field; the id itself when nothing is known.
export const displayName = (u, id) =>
  (u && (u.name || u.fullName || u.displayName || u.email)) || id;

// Strip spaces and punctuation so the number is dialable from a tel: link (R18: Leads.js and
// Payouts.js both need this now, so it lives here rather than in either page).
export const dialable = (phone) => String(phone || "").replace(/[^\d+]/g, "");

// ── Tickets waiting on us ──
// `who` is known from the ticket itself when reportLesson.js snapshotted a
// learnerName; otherwise `whoId` names the doc to look up (the learner on a
// lesson ticket, the raiser on a request), and withNames() fills `who` in.
export const ticketRow = (ticket, now) => ({
  id: ticket.id,
  label: ticketNumberLabel(ticket).label,
  kind: kindLabel(ticket.kind),
  who: ticket.learnerName || null,
  whoId: ticket.learnerName ? null : ticket.studentId || ticket.raisedBy || null,
  age: ageLabel(ticket.raisedAt, now),
  raisedAt: toDate(ticket.raisedAt),
  to: `/inbox/${ticket.id}`,
});

// ── Lessons needing a decision ──
// Two sources, one row shape. A report is a reported_lesson ticket that is still
// open or waiting_user (the caller filters status); it carries the lesson's
// snapshot (bookingId, parties, start, reason — reportLesson.js ticketFor), so
// no booking read is needed for the row.
export const lessonRowFromTicket = (ticket, now) => {
  if (!ticket || ticket.kind !== "reported_lesson" || !ticket.bookingId) return null;
  const reason = ticket.evidence && ticket.evidence.reason;
  return {
    id: ticket.bookingId,
    learnerName: ticket.learnerName || null,
    learnerId: ticket.learnerName ? null : ticket.studentId || null,
    isChild: false,
    tutorId: ticket.teacherId || null,
    when: whenLabel(ticket.startTimeUTC),
    whenAt: toDate(ticket.startTimeUTC),
    reported: true,
    why: `Reported: ${truncate(reason, 80)}`,
    to: `/lessons/${ticket.bookingId}`,
  };
};

// A Pending lesson whose start is before pendingCutoff(now): it has ended and
// nobody finished it. When lessonLifecycle.js already raised a ticket the row
// says so — that ticket is also in the tickets group, by design (two doors).
export const lessonRowFromBooking = (booking, now) => {
  const ended = toDate(booking.endTimeUTC) || toDate(booking.startTimeUTC);
  return {
    id: booking.id,
    learnerName: null,
    learnerId: booking.studentId || null,
    isChild: Boolean(booking.learner && booking.learner.kind === "child"),
    tutorId: booking.teacherId || null,
    when: whenLabel(booking.startTimeUTC),
    whenAt: toDate(booking.startTimeUTC),
    reported: false,
    why: `Ended ${ageLabel(ended, now)} ago, still Pending${booking.ticketId ? " · ticket raised" : ""}`,
    to: `/lessons/${booking.id}`,
  };
};

const whenMs = (row) => (row.whenAt ? row.whenAt.getTime() : 0);

// One row per lesson. A lesson that is both reported and unfinished shows as
// reported — the report is the more specific reason. Newest first; a row with
// no usable date sinks to the bottom rather than floating on NaN.
export const mergeLessonRows = (reported, pending) => {
  const byId = new Map();
  pending.forEach((row) => byId.set(row.id, row));
  reported.forEach((row) => byId.set(row.id, row));
  return [...byId.values()].sort((a, b) => whenMs(b) - whenMs(a));
};

// Reported rows are fetched in full; pending ones are a count from the server
// plus a window. The overlap we can subtract is the overlap we can see — a
// reported lesson that is also Pending, ended, and older than the window is
// counted twice. Rare (both windows are short-lived: someone resolves them)
// and self-correcting the moment either side is dealt with.
export const lessonCount = ({ reported, pending, pendingCount }) => {
  const seen = new Set(pending.map((row) => row.id));
  const overlap = reported.filter((row) => seen.has(row.id)).length;
  return reported.length + pendingCount - overlap;
};

// ── Tutors awaiting verification ──
// Onboarding writes verifyStatus "pending" with no submitted-at of its own
// (121islamapp src/utils/teacherOnboarding.js), so "submitted" is the doc's
// last update — verifyRequestedAt if a later app ever writes one, else
// updatedAt, else createdAt.
export const verificationRow = (teacher) => {
  const row = toVerificationRow(teacher);
  const submittedAt =
    toDate(teacher.verifyRequestedAt) || toDate(teacher.updatedAt) || toDate(teacher.createdAt);
  return {
    id: row.id,
    name: row.name,
    subjects: row.subjects.length ? row.subjects.join(", ") : "-",
    submitted: submittedAt ? dayLabel(submittedAt) : "-",
    submittedAt,
    to: `/verification?teacher=${row.id}`,
  };
};

export const sortVerificationRows = (rows) =>
  [...rows].sort((a, b) => {
    if (!a.submittedAt && !b.submittedAt) return 0;
    if (!a.submittedAt) return 1;
    if (!b.submittedAt) return -1;
    return b.submittedAt.getTime() - a.submittedAt.getTime();
  });

// ── Payouts due (admin+) ──
// The period that closed last, as the Payouts page names it (037): "Aug · 26 Jul – 25 Aug".
export const periodText = (period) => `${periodLabel(period)} · ${periodRange(period)}`;

// ponytail: `unpaidEarning` is the tutor's running owed total, not the closed
// period's share of it — a per-period split needs the bookings scan Reports
// does (slice 7). The label says which period is due; the figure is what Payouts
// will actually pay.
export const payoutRow = (teacher, period) => ({
  id: teacher.id,
  name: displayName(teacher, teacher.id),
  amount: egp(teacher.unpaidEarning),
  period,
  to: "/payouts",
});

// ── Names, resolved once for the rows that render ──
export const peopleIds = (rows) => [
  ...new Set(
    rows
      .flatMap((row) => [row.whoId, row.learnerId, row.tutorId])
      .filter(Boolean)
  ),
];

export const withNames = (row, nameOf) => {
  const next = { ...row };
  if (row.who == null && row.whoId) next.who = nameOf(row.whoId);
  if (row.learnerId || row.learnerName) {
    const learner = row.learnerName || nameOf(row.learnerId);
    next.learner = row.isChild ? `${learner} (child)` : learner;
  }
  if (row.tutorId) next.tutor = nameOf(row.tutorId);
  return next;
};

// ── What a row says, per group ──
export const rowText = (groupKey, row) => {
  switch (groupKey) {
    case "tickets":
      return { primary: `${row.label} · ${row.kind}`, secondary: `${row.who || "—"} · ${row.age} ago` };
    case "verification":
      return { primary: row.name, secondary: `${row.subjects} · submitted ${row.submitted}` };
    case "lessons":
      return { primary: `${row.learner || "—"} · ${row.tutor || "—"}`, secondary: `${row.when} · ${row.why}` };
    case "payouts":
      return { primary: `${row.name} · ${row.amount}`, secondary: row.period };
    default:
      return { primary: "", secondary: "" };
  }
};

// The four groups, in the spec's order. Each is gated by the permission of the
// page it opens: a group nobody can open is a card whose rows and "View all"
// dead-end on the permission screen (an accountant has neither VIEW_INBOX nor
// REVIEW_VERIFICATION). Payouts additionally means "can record one", which is
// why it is EDIT_FINANCIAL and not VIEW_FINANCIAL — admins and, since 053 D2,
// the accountant; a moderator who cannot pay has nothing to do with the row.
export const GROUPS = [
  { key: "tickets", title: "Tickets waiting on us", viewAll: "/inbox", permission: permissions.VIEW_INBOX },
  { key: "verification", title: "Tutors awaiting verification", viewAll: "/verification", permission: permissions.REVIEW_VERIFICATION },
  { key: "lessons", title: "Lessons needing a decision", viewAll: "/lessons", permission: permissions.VIEW_LESSONS },
  { key: "payouts", title: "Payouts due", viewAll: "/payouts", permission: permissions.EDIT_FINANCIAL },
];

export const visibleGroups = (user) =>
  GROUPS.filter((g) => !g.permission || hasPermission(user, g.permission));
